import * as React from "react";
import { useForm } from "react-hook-form";
import { DialogContent, Grid, Typography, FormControl, TextField, DialogActions } from "@mui/material";
import DialogHESP from "../../DialogHESP/DialogHESP";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import { GetChannelQuery_channel } from "../Overview/__generated__/GetChannelQuery";
import { useMutation } from "@apollo/react-hooks";
import DialogFooterWithLoading from "../../Loading/DialogFooterWithLoading";
import gql from "graphql-tag";
import { GET_CHANNEL_QUERY, GET_CHANNELS_ORGANIZATION_QUERY } from "../Overview/channelQueriesMutations";
import { UpdateExternalIdMutation, UpdateExternalIdMutationVariables } from "./__generated__/UpdateExternalIdMutation";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../BillingDetails/AddPaymentMethodDialog";

export const UPDATE_EXTERNAL_ID_MUTATION = gql`
    mutation UpdateExternalIdMutation($input: UpdateChannelExternalIdInput!) {
        updateChannelExternalId(input: $input) {
            channelId
        }
    }
`;

interface Props {
    open: boolean;
    channel: GetChannelQuery_channel;
    onClose: () => void;
    onSuccess: () => void;
    onError: () => void;
}

interface FormData {
    externalId: string;
}

export default function EditExternalIdDialog({ open, channel, onClose, onSuccess, onError }: Props) {
    const { handleSubmit, register, errors } = useForm<FormData>();

    const [updateExternalId, { loading }] = useMutation<UpdateExternalIdMutation, UpdateExternalIdMutationVariables>(
        UPDATE_EXTERNAL_ID_MUTATION,
    );

    const { metadata, channelId, organizationId } = channel;

    const editChannel = handleSubmit(async ({ externalId }: FormData) => {
        try {
            await updateExternalId({
                variables: { input: { channelId, organizationId: channel.organizationId, externalId } },
                refetchQueries: [
                    {
                        query: GET_CHANNEL_QUERY,
                        variables: {
                            channelId,
                            organizationId: channel.organizationId,
                        },
                    },
                    {
                        query: GET_CHANNELS_ORGANIZATION_QUERY,
                        variables: {
                            organizationId,
                        },
                    },
                ],
            });
            onSuccess();
        } catch (e: any) {
            toast.error(e.graphQLErrors[0].message ?? "Something went wrong 😞", TOAST_SETTINGS);
        }
        onClose();
    });

    return (
        <DialogHESP
            title={"Edit external ID"}
            open={open}
            onClose={onClose}
            content={
                <form onSubmit={handleSubmit((_ignore, e) => editChannel(e))}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="External ID" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={metadata.externalId}
                                        name="externalId"
                                        inputRef={register({
                                            required: "ExternalID is required",
                                        })}
                                    />
                                    <Typography variant="subtitle2" color="error">
                                        {errors.externalId && errors.externalId.message}
                                    </Typography>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <DialogFooterWithLoading
                            isLoading={loading}
                            onCancelClick={onClose}
                            defaultText={"Save"}
                            loadingText={"Saving..."}
                        />
                    </DialogActions>
                </form>
            }
        />
    );
}
